import {
    Box,
    SimpleGrid,
    Text,
    Flex,
    Icon,
    Container,
    Alert,
    AlertIcon,
} from "@chakra-ui/react";
import PageLoader from "./components/PageLoader";
import {
    FaUsers,
    FaChartLine,
    FaUserCheck,
    FaExchangeAlt,
    FaChartBar,
    FaMoneyBillWave,
} from "react-icons/fa";
import axios from "axios";
import { API_BASE } from "./config";
import { useEffect, useState } from "react";

function Dashboard() {
    const [users, setUsers] = useState<any>(0);
    const [tokens, setTokens] = useState<any>([]);
    const [dashboardStats, setDashboardStats] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const fetchUsers = async () => {
        try {
            const res = await axios.get(
                `${API_BASE}/api/admin/user/refferal?page=1&limit=10000`,
            );
            setUsers(res?.data?.data?.length || 0);
        } catch (err: any) {
            console.error("Error fetching users:", err);
        }
    };

    const fetchTokens = async () => {
        try {
            const res = await axios.get(
                `${API_BASE}/api/admin/orders?page=1&limit=10000`,
            );
            setTokens(res?.data?.data?.orders || []);
        } catch (err: any) {
            console.error("Error fetching tokens:", err);
        }
    };

    const fetchDashboardStats = async () => {
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(
                `${API_BASE}/api/admin/dashboard/stats`,
            );
            console.log("Dashboard stats:", res.data);
            setDashboardStats(res?.data?.data || null);
        } catch (err: any) {
            console.error("Error fetching dashboard stats:", {
                message: err.message,
                response: err.response?.data,
                status: err.response?.status,
                url: err.config?.url,
            });

            const errorMessage =
                err.response?.status === 404
                    ? "Dashboard endpoint not found. Please restart the backend server."
                    : err.response?.data?.message ||
                      err.message ||
                      "Error loading dashboard stats";

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        fetchDashboardStats();
    }, []);

    useEffect(() => {
        if (users) {
            fetchTokens();
        }
    }, [users]);

    // Format number with K/M/B/T suffixes for large numbers
    const formatNumber = (num: number | string) => {
        // Convert string to number if needed
        let value = typeof num === "string" ? parseFloat(num) : num;

        if (!value || value === 0 || isNaN(value) || !isFinite(value))
            return "0.00";

        // If number is extremely small (less than 0.000001), show as 0.00
        if (Math.abs(value) < 0.000001) {
            return "0.00";
        }

        // Handle extremely large numbers (likely in wei - 10^18 decimals)
        // Numbers like 7.55e+24 need to be divided by 10^18
        if (Math.abs(value) >= 1e18) {
            value = value / 1e18;
        }

        // Format with appropriate suffix
        const absValue = Math.abs(value);
        if (absValue >= 1e12) {
            return (value / 1e12).toFixed(2) + "T";
        } else if (absValue >= 1e9) {
            return (value / 1e9).toFixed(2) + "B";
        } else if (absValue >= 1e6) {
            return (value / 1e6).toFixed(2) + "M";
        } else if (absValue >= 1e3) {
            return (value / 1e3).toFixed(2) + "K";
        }

        // For smaller numbers, show with 2 decimal places
        return value.toFixed(2);
    };

    // Format percentage change
    const formatChange = (change: number) => {
        const sign = change >= 0 ? "+" : "";
        return `${sign}${change.toFixed(2)}%`;
    };

    const statCards = [
        {
            label: "Total Wallet Connects",
            value: users || 0,
            icon: FaUsers,
            color: "blue.500",
            change: "+12%",
            isIncrease: true,
        },
        {
            label: "Active Wallets",
            value: users || 0,
            icon: FaUserCheck,
            color: "green.500",
            change: "+8%",
            isIncrease: true,
        },
        {
            label: "Total Trades",
            value: tokens?.length || 0,
            icon: FaExchangeAlt,
            color: "purple.500",
            change: "+24%",
            isIncrease: true,
        },
        {
            label: "Trading Volume (This Month)",
            value:
                dashboardStats?.tradingVolume?.current !== undefined
                    ? `$${formatNumber(
                          dashboardStats.tradingVolume.current / 10 ** 30,
                      )}`
                    : "$0.00",
            icon: FaChartLine,
            color: "red.500",
            change:
                dashboardStats?.tradingVolume?.changePercent !== undefined
                    ? formatChange(
                          parseFloat(
                              dashboardStats.tradingVolume.changePercent,
                          ),
                      )
                    : "0%",
            isIncrease:
                dashboardStats?.tradingVolume?.changePercent !== undefined
                    ? parseFloat(dashboardStats.tradingVolume.changePercent) >=
                      0
                    : true,
        },
        {
            label: "Avg Volume/Trade (This Month)",
            value: dashboardStats?.avgVolumePerUser
                ? `$${formatNumber(
                      dashboardStats.avgVolumePerUser.current /
                          10 ** 30 /
                          tokens?.length,
                  )}`
                : "$0.00",
            icon: FaChartBar,
            color: "orange.500",
            change: dashboardStats?.avgVolumePerUser
                ? formatChange(
                      parseFloat(dashboardStats.avgVolumePerUser.changePercent),
                  )
                : "0%",
            isIncrease: dashboardStats?.avgVolumePerUser
                ? parseFloat(dashboardStats.avgVolumePerUser.changePercent) >= 0
                : true,
        },
        {
            label: "Fees Generated (This Month)",
            value: dashboardStats?.feesGenerated
                ? `$${formatNumber(dashboardStats.feesGenerated.current)}`
                : "$0.00",
            icon: FaMoneyBillWave,
            color: "teal.500",
            change: dashboardStats?.feesGenerated
                ? formatChange(
                      parseFloat(dashboardStats.feesGenerated.changePercent),
                  )
                : "0%",
            isIncrease: dashboardStats?.feesGenerated
                ? parseFloat(dashboardStats.feesGenerated.changePercent) >= 0
                : true,
        },
    ];

    return (
        <Container maxW="7xl" py={6}>
            {/* Header */}
            <Box mb={8}>
                <Text fontSize="3xl" fontWeight="700" color="white" mb={2}>
                    Dashboard Overview
                </Text>
                <Text color="gray.400" fontSize="lg">
                    Real-time analytics and key performance metrics
                </Text>
            </Box>

            {loading && <PageLoader message="Loading dashboard stats..." />}

            {error && (
                <Alert status="error" mb={6}>
                    <AlertIcon />
                    {error}
                </Alert>
            )}

            {/* Stats Grid */}
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {statCards.map((stat, index) => (
                    <Box
                        key={index}
                        bg="gray.800"
                        border="1px solid"
                        borderColor="gray.700"
                        borderRadius="12px"
                        p={6}
                        position="relative"
                        overflow="hidden"
                        _hover={{
                            borderColor: "gray.600",
                            transform: "translateY(-2px)",
                            boxShadow: "xl",
                        }}
                        transition="all 0.2s ease-in-out"
                    >
                        {/* Gradient overlay */}
                        <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            height="2px"
                            bgGradient={`linear(to-r, ${
                                stat.color
                            }, ${stat.color.replace(".500", ".300")})`}
                        />

                        <Flex justify="space-between" align="start" mb={4}>
                            <Box>
                                <Icon
                                    as={stat.icon}
                                    w={8}
                                    h={8}
                                    color={stat.color}
                                    mb={3}
                                />
                                <Text
                                    fontSize="sm"
                                    fontWeight="500"
                                    color="gray.400"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                >
                                    {stat.label}
                                </Text>
                            </Box>
                            <Box textAlign="right">
                                <Flex
                                    align="center"
                                    color={
                                        stat.isIncrease
                                            ? "green.400"
                                            : "red.400"
                                    }
                                >
                                    <Text fontSize="sm" fontWeight="500">
                                        {stat.isIncrease ? "↗" : "↘"}{" "}
                                        {stat.change}
                                    </Text>
                                </Flex>
                            </Box>
                        </Flex>

                        <Text
                            fontSize="2xl"
                            fontWeight="700"
                            color="white"
                            lineHeight={1}
                        >
                            {typeof stat.value === "string"
                                ? stat.value
                                : stat.value.toLocaleString()}
                        </Text>

                        <Text fontSize="xs" color="gray.500" mt={1}>
                            vs last month
                        </Text>
                    </Box>
                ))}
            </SimpleGrid>

            {/* Additional Info */}
            <Box
                mt={8}
                p={6}
                bg="gray.800"
                borderRadius="12px"
                border="1px solid"
                borderColor="gray.700"
            >
                <Flex align="center" justify="space-between">
                    <Box>
                        <Text
                            fontSize="lg"
                            fontWeight="600"
                            color="white"
                            mb={1}
                        >
                            System Status
                        </Text>
                        <Text color="gray.400" fontSize="sm">
                            All systems operational
                        </Text>
                    </Box>
                    <Box
                        w={3}
                        h={3}
                        bg="green.400"
                        borderRadius="full"
                        boxShadow="0 0 10px rgba(72, 187, 120, 0.5)"
                    />
                </Flex>
            </Box>
        </Container>
    );
}

export default Dashboard;
